import {
  ChangeEvent,
  ChangeEventHandler,
  FC,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { InputMask, InputMaskProps } from '@react-input/mask';

import { Button, Icon, IconSvg } from '@/components';

import style from './input.module.scss';

export interface MaskInputProps extends InputMaskProps {
  autoComplete?: 'off' | 'new-password' | 'old-password' | '' | undefined;
  checked?: boolean;
  children?: ReactNode | string;
  className?: string;
  disabled?: boolean;
  feedback?: string;
  id?: string;
  inFooter?: boolean;
  required?: boolean;
  placeholder?: string;
  inputLabel?: string;
  isClearable?: boolean;
  isVisibilityControl?: boolean;
  showCalendarIcon?: boolean;
  isInvalid?: boolean;
  rows?: number;
  icon?: ReactNode;
  name?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onClear?: () => void;
  type?:
    | 'checkbox'
    | 'email'
    | 'password'
    | 'radio'
    | 'search'
    | 'textarea'
    | 'tel'
    | 'date'
    | 'textdate';
  value?: any;
  mask?: string;
  ref?: RefObject<HTMLInputElement>;
}

function maskedDateToIso(masked: string): string {
  if (typeof masked !== 'string' || !masked || masked.length < 10) return '';
  const parts = masked.split('/');
  if (parts.length !== 3) return '';
  const [mm, dd, yyyy] = parts;
  if (!mm || !dd || !yyyy || yyyy.length < 4) return '';
  return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
}

function isoToMaskedDate(iso: string): string {
  if (typeof iso !== 'string' || !iso || iso.length < 10) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return '';
  return `${m}/${d}/${y}`;
}

const Input: FC<MaskInputProps> = ({
  autoComplete = '',
  checked,
  children,
  type,
  disabled,
  isInvalid,
  feedback,
  className,
  inFooter,
  id,
  icon,
  placeholder = '',
  inputLabel,
  isVisibilityControl,
  showCalendarIcon = false,
  value,
  name,
  isClearable,
  required = false,
  onChange,
  onClear,
  mask = '',
  inputMode,
  ref,
  ...props
}) => {
  const nativeDateRef = useRef<HTMLInputElement>(null);
  const [inputType, setInputType] = useState(type);

  const isTypeCheck = ['radio', 'checkbox'].includes(
    type as
      | 'search'
      | 'textarea'
      | 'checkbox'
      | 'radio'
      | 'tel'
      | 'email'
      | 'date'
      | 'password'
      | 'textdate',
  );

  const [inputValue, setInputValue] = useState('');

  const isShowLabel = (inputLabel || children) && (inputValue || value);
  const [inputPlaceholder, setInputPlaceholder] = useState(placeholder);

  const handleClear = () => {
    setInputValue('');
    onClear?.();
  };

  const handleShowPass = () => {
    if (inputType === 'password') {
      setInputType(undefined);
      return;
    }

    setInputType('password');
  };

  const handleFocus = () => {
    if (type === 'date') {
      setInputPlaceholder('mm/dd/yyyy');
    }
  };

  const handleBlur = () => {
    if (type === 'date') {
      setInputPlaceholder(placeholder);
    }
  };

  const handleCalendarClick = useCallback(() => {
    if (nativeDateRef.current) {
      if (typeof nativeDateRef.current.showPicker === 'function') {
        nativeDateRef.current.showPicker();
      } else {
        nativeDateRef.current.focus();
      }
    }
  }, []);

  const handleNativeDateChange: ChangeEventHandler<HTMLInputElement> =
    useCallback(
      e => {
        const iso = e.target.value;
        const masked = isoToMaskedDate(iso);
        if (masked && onChange) {
          onChange({
            ...e,
            target: { ...e.target, value: masked, name: name ?? '' },
          } as ChangeEvent<HTMLInputElement>);
        }
      },
      [onChange, name],
    );

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const hasRightButton =
    isClearable || isVisibilityControl || (type === 'date' && showCalendarIcon);
  const nativeDateValue =
    type === 'date' && showCalendarIcon && typeof value === 'string' && value
      ? maskedDateToIso(value)
      : '';

  return (
    <div className={style.input_container}>
      <label
        className={classNames(style.input, className, {
          [style.input__textarea]: inputType === 'textarea',
          [style.input__disabled]: disabled,
          [style.input__invalid]: isInvalid,
          [style.input__footer]: inFooter,
          [style.input__type_text]: !isTypeCheck,
        })}
      >
        {icon && <span className={style.input__addortment}>{icon}</span>}
        {(inputLabel || children) &&
          (inputValue || value || inputType === 'checkbox') && (
            <span
              className={classNames(style.input__label, {
                // eslint-disable-next-line max-len
                [style.input__label_search]: inputType === 'search',
                // eslint-disable-next-line max-len
                [style.input__label_checkbox]: inputType === 'checkbox',
                [style.input__label_icon]: icon,
              })}
            >
              {inputLabel || children}
            </span>
          )}
        {type === 'date' && showCalendarIcon && (
          <input
            aria-hidden="true"
            className={style.input__native_date}
            onChange={handleNativeDateChange}
            ref={nativeDateRef}
            tabIndex={-1}
            type="date"
            value={nativeDateValue}
          />
        )}
        <InputMask
          aria-invalid={isInvalid}
          autoComplete={autoComplete}
          checked={checked}
          className={classNames(style.input__control, {
            [style.input__control_icon]: icon,
            [style.input__control_btn]: hasRightButton,
            [style.input__control_btn_icon]: hasRightButton && icon,
            [style.input__control_pressed]: isShowLabel,
            [style.input__control_icon_pressed]: isShowLabel && icon,
            [style.input__control_btn_pressed]: hasRightButton && isShowLabel,
            [style.input__control_btn_icon_pressed]:
              hasRightButton && isShowLabel && icon,
          })}
          disabled={disabled}
          id={id}
          inputMode={type === 'date' ? 'numeric' : inputMode}
          mask={mask}
          name={name}
          onBlur={handleBlur}
          onChange={onChange}
          onFocus={handleFocus}
          placeholder={inputPlaceholder}
          ref={ref}
          required={required}
          type={type !== 'date' ? inputType : undefined}
          value={isClearable ? inputValue : value}
          {...props}
        />
        {isClearable && (
          <Button
            className={style.input__btn}
            color="link"
            onClick={handleClear}
          >
            <IconSvg name="close" />
          </Button>
        )}
        {isVisibilityControl && (
          <Button className={style.input__btn} onClick={handleShowPass}>
            <IconSvg
              name={inputType === 'password' ? 'showpass' : 'hidepass'}
            />
          </Button>
        )}
        {type === 'date' && showCalendarIcon && (
          <Button
            aria-label="Open date picker"
            className={classNames(style.input__btn, style.input__btn_calendar)}
            color="link"
            onClick={handleCalendarClick}
            type="button"
          >
            <IconSvg name="calendar" />
          </Button>
        )}
      </label>
      {isInvalid && feedback && (
        <p className={style.input__feedback}>
          <Icon name="info" /> {feedback}
        </p>
      )}
    </div>
  );
};

export default Input;
